import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { DeniedState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import {
  CredentialStatus,
  ProviderForm,
  emptyProviderFormState,
  isForbidden,
  providerFormStateFrom,
  toError,
} from '@/modules/ai/shared';
import type { ProviderFormResult } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import type { ProviderSummary, ProviderTestResult } from '@/api/schemas/ai';

/**
 * The tenant's own provider.
 *
 * It is the same form the platform console uses — the fields mean the same
 * thing on both sides, and a tenant that reads the platform's provider screen
 * should recognise this one.
 *
 * The credential is write-only here too: the field takes a key, the key is
 * sent once, and only the status is ever shown again.
 */
export type TenantProviderFormProps = {
  /** The tenant's provider, or null when it has none yet. */
  tenantProviderId: string | null;
  onChanged: () => void;
};

export function TenantProviderForm({
  tenantProviderId,
  onChanged,
}: TenantProviderFormProps): ReactNode {
  const load = useCallback(async () => {
    if (tenantProviderId === null) return null;
    return aiApi.getTenantProvider(tenantProviderId);
  }, [tenantProviderId]);

  const { data, error, isLoading, reload } = useAsyncResource(load);

  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [testResult, setTestResult] = useState<ProviderTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  if (tenantProviderId !== null) {
    const surface = tenantProviderSurface({ isLoading, error, onRetry: reload });
    if (surface !== null) return surface;
  }

  const provider = data;
  const context: TenantProviderContext = {
    setIsSaving,
    setSubmitError,
    setTestResult,
    setIsTesting,
    onChanged,
    reload,
  };

  return (
    <div className="space-y-6">
      {provider === null ? (
        <NoTenantProvider />
      ) : (
        <TenantProviderStatus
          provider={provider}
          testResult={testResult}
          isTesting={isTesting}
          onTest={() => {
            void testProvider(context, provider);
          }}
        />
      )}

      <ProviderForm
        // Remounting on a new provider version re-seeds the form from the
        // server's copy, so a saved change is shown as saved.
        key={provider === null ? 'new-provider' : provider.version}
        initialValues={
          provider === null ? emptyProviderFormState() : providerFormStateFrom(provider)
        }
        submitLabel={provider === null ? 'Create provider' : 'Save provider'}
        isSubmitting={isSaving}
        submitError={submitError}
        credentialLabel={provider === null ? 'API credential' : 'Replace credential'}
        credentialHint="Write-only. It is sent once and never shown again — only its status, version and last-rotation date are kept."
        onSubmit={(result) => {
          void submitProvider(context, provider, result);
        }}
      />
    </div>
  );
}

type TenantProviderContext = {
  setIsSaving: (busy: boolean) => void;
  setSubmitError: (error: Error | null) => void;
  setTestResult: (result: ProviderTestResult | null) => void;
  setIsTesting: (busy: boolean) => void;
  onChanged: () => void;
  reload: () => void;
};

async function submitProvider(
  context: TenantProviderContext,
  provider: ProviderSummary | null,
  result: ProviderFormResult,
): Promise<void> {
  context.setIsSaving(true);
  context.setSubmitError(null);
  try {
    await saveProvider(provider, result);
    context.onChanged();
    context.reload();
  } catch (cause) {
    context.setSubmitError(toError(cause));
  } finally {
    context.setIsSaving(false);
  }
}

async function testProvider(
  context: TenantProviderContext,
  provider: ProviderSummary,
): Promise<void> {
  context.setIsTesting(true);
  context.setSubmitError(null);
  try {
    context.setTestResult(await aiApi.testTenantProvider(provider.id));
  } catch (cause) {
    context.setSubmitError(toError(cause));
  } finally {
    context.setIsTesting(false);
  }
}

/** Creates the provider when there is none, updates it when there is one. */
async function saveProvider(
  provider: ProviderSummary | null,
  result: ProviderFormResult,
): Promise<void> {
  if (provider === null) {
    const created = await aiApi.createTenantProvider(result.provider);
    if (result.apiKey !== null) {
      await aiApi.rotateTenantCredential(created.id, { apiKey: result.apiKey });
    }
    return;
  }

  await aiApi.updateTenantProvider(provider.id, result.provider, provider.version);
  if (result.apiKey !== null) {
    await aiApi.rotateTenantCredential(provider.id, { apiKey: result.apiKey });
  }
}

type TenantProviderSurfaceParams = {
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
};

function tenantProviderSurface(params: TenantProviderSurfaceParams): ReactNode | null {
  if (params.isLoading) {
    return <LoadingSkeleton shape="detail" rows={4} label="Loading your provider" />;
  }
  if (isForbidden(params.error)) {
    return (
      <DeniedState
        title="Not permitted for your role"
        message="You cannot read this workspace's AI provider."
      />
    );
  }
  if (params.error) {
    return (
      <ErrorState
        title="Could not load your provider"
        message={params.error.message}
        onRetry={params.onRetry}
      />
    );
  }
  return null;
}

type TenantProviderStatusProps = {
  provider: ProviderSummary;
  testResult: ProviderTestResult | null;
  isTesting: boolean;
  onTest: () => void;
};

function TenantProviderStatus(props: TenantProviderStatusProps): ReactNode {
  return (
    <section className="space-y-3 rounded-lg border border-border p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-medium">Your provider</h3>
        <Button variant="secondary" loading={props.isTesting} onClick={props.onTest}>
          Test connection
        </Button>
      </div>
      {/*
       * WHICH PROVIDER THIS IS.
       *
       * The tenant's own screen is the only place it can see the provider it has
       * configured, so it has to say which one it is — the name, the model it calls and
       * where it calls it. A panel that reported only "a credential is configured" would
       * leave a tenant with two providers unable to tell which one is in front of them.
       */}
      <div className="flex flex-col">
        <span className="font-medium text-content">{props.provider.name}</span>
        <span className="text-xs text-content-muted">
          {props.provider.modelId} · {props.provider.baseUrl}
        </span>
      </div>
      <CredentialStatus provider={props.provider} />
      {props.testResult ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={props.testResult.state === 'ok' ? 'positive' : 'critical'}>
            {props.testResult.state === 'ok' ? 'Passed' : 'Failed'}
          </Badge>
          {props.testResult.detail ? (
            <span className="text-sm text-content-muted">{props.testResult.detail}</span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function NoTenantProvider(): ReactNode {
  return (
    <p className="text-sm text-content-muted">
      You have no provider of your own yet. Add one below, or keep using the platform&apos;s
      provider.
    </p>
  );
}
